// Core imports
import { Fragment, useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// Component imports
import { DataGrid, GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarContainer, getGridBooleanOperators, getGridNumericOperators, getGridStringOperators } from '@mui/x-data-grid';
import { Row, Col, Typography, Tag, Space, Input } from 'antd';
import { SearchOutlined, ClearOutlined, DatabaseOutlined } from '@ant-design/icons';
import Chip from '@mui/material/Chip';
import NdeTypeTag from '../Tags/NdeTypeTag';

// Misc imports
import { ThemeProvider } from '@mui/material/styles';
import MultiFilterPanel from './MultiFilterPanel';
import { fetchNdes } from '../../store/ndes/ndesThunks';
import { useTranslation } from 'react-i18next';
import {
  NAV_NDES_VIEW, NDE_TYPES, BINARY_ANSWERS, TERNARY_ANSWERS, ENVIRONMENTS,
  TIME_PERCEPTIONS, REALITY_PERCEPTIONS,
  FELT_JUDGED, GENDERS, SEXUALITIES
} from '../../constants';
import PropTypes from 'prop-types';
import { createTheme } from '@mui/material/styles';
import replaceUrlParams from '../../utils/misc/replaceUrlParams';

const { Title, Paragraph, Text } = Typography;

// Default visible columns - the most important ones shown initially
const DEFAULT_VISIBLE_FIELDS = ['title', 'provider', 'type', 'text', 'deathCause', 'environment', 'graysonScale'];

const SUGGESTED_SEARCHES = [
  {
    label: 'Non-heterosexual experiences',
    filters: [{ field: 'sexuality', operator: 'isAnyOf', value: ['homosexual', 'bisexual', 'asexual', 'other'] }],
    extraColumns: ['sexuality', 'gender']
  },
  {
    label: 'Future visions that came true',
    filters: [
      { field: 'hadVisionsOfFuture', operator: 'is', value: 'yes' },
      { field: 'visionsCameTrue', operator: 'is', value: 'yes' }
    ],
    extraColumns: ['hadVisionsOfFuture', 'visionsCameTrue', 'hadVisionsOfFutureText']
  },
  {
    label: 'Saw animals',
    filters: [{ field: 'entityTypes', operator: 'contains', value: 'animals' }],
    extraColumns: ['entityTypes', 'feltOtherEntities']
  },
  {
    label: 'Healed after NDE',
    filters: [{ field: 'wasHealed', operator: 'is', value: 'yes' }],
    extraColumns: ['wasHealed', 'wasHealedText']
  },
  {
    label: 'Escaped a distressing NDE',
    filters: [
      { field: 'type', operator: 'is', value: 'distressing' },
      { field: 'escapedNegativeNde', operator: 'is', value: 'yes' }
    ],
    extraColumns: ['escapedNegativeNde', 'escapedNegativeNdeText']
  }
];

const CustomToolbar = ({ onClearFilters, hasActiveFilters, activeFilterCount, totalResults, onQuickSearch, quickSearchValue }) => (
  <GridToolbarContainer sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f0f0f0', gap: 1 }}>
    <Row align="middle" justify="space-between" style={{ width: '100%' }} gutter={[12, 8]}>
      <Col>
        <Space size={8} wrap>
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          {hasActiveFilters && (
            <Chip
              icon={<ClearOutlined style={{ fontSize: 12 }} />}
              label={`Clear ${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''}`}
              size="small"
              onClick={onClearFilters}
              onDelete={onClearFilters}
              variant="outlined"
              color="primary"
              sx={{ height: 30, fontSize: 13 }}
            />
          )}
        </Space>
      </Col>
      <Col>
        <Space size={12} align="center">
          {totalResults != null && (
            <Tag icon={<DatabaseOutlined />} style={{ marginRight: 0, fontSize: 12 }}>
              {totalResults.toLocaleString()} results
            </Tag>
          )}
          <Input
            placeholder="Quick search text..."
            prefix={<SearchOutlined style={{ color: '#bbb' }} />}
            allowClear
            size="small"
            value={quickSearchValue}
            onChange={(e) => onQuickSearch(e.target.value)}
            onPressEnter={(e) => onQuickSearch(e.target.value)}
            style={{ width: 200 }}
          />
        </Space>
      </Col>
    </Row>
  </GridToolbarContainer>
);

CustomToolbar.propTypes = {
  onClearFilters: PropTypes.func.isRequired,
  hasActiveFilters: PropTypes.bool.isRequired,
  activeFilterCount: PropTypes.number.isRequired,
  totalResults: PropTypes.number,
  onQuickSearch: PropTypes.func.isRequired,
  quickSearchValue: PropTypes.string.isRequired
};

const primary1 = 'rgb(145, 149, 252)';
const primary2 = 'rgb(191, 193, 255)';
//const primary3 = 'rgb(224, 226, 255)';
const primary4 = 'rgb(239, 239, 255)';

const theme = createTheme({
  palette: {
    primary: {
      main: primary1
    },
    action: {
      hover: primary4
    }
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: false
      }
    },
    MuiTouchRipple: {
      styleOverrides: {
        ripple: {
          color: primary2
        }
      }
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 'none',
          fontSize: 13,
          borderRadius: 12,
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#fafafa',
            borderBottom: '2px solid #f0f0f0',
            fontSize: 12,
            fontWeight: 600,
            color: '#777',
            letterSpacing: '0.03em',
            textTransform: 'uppercase'
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #f5f5f5',
            padding: '14px 12px',
            display: 'flex',
            alignItems: 'center',
            transition: 'background-color 0.15s ease',
            '&:focus, &:focus-within': {
              outline: 'none'
            }
          },
          '& .MuiDataGrid-row': {
            transition: 'background-color 0.15s ease',
            '&:nth-of-type(even)': {
              backgroundColor: '#fcfcff'
            },
            '&:hover': {
              backgroundColor: primary4,
              cursor: 'pointer'
            },
            '&.Mui-selected': {
              backgroundColor: `${primary4} !important`
            }
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '2px solid #f0f0f0'
          },
          '& .MuiDataGrid-columnSeparator': {
            visibility: 'hidden'
          }
        }
      }
    }
  }
});

// TODO: Add description to all columns
const NdesTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Store selectors
  const { ndes, isFetchingNdes } = useSelector((state) => state.ndes);

  // Sort state (server-side) — initialised from URL so it survives a browser refresh
  const [sortModel, setSortModel] = useState(() => {
    const sortParam = searchParams.get('sort');
    if (sortParam) {
      try {
        const parsed = JSON.parse(sortParam);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch (e) { /* ignore parse errors */ }
    }
    return [];
  });

  // Local state for pagination and filters
  const [paginationModel, setPaginationModel] = useState({
    page: Number(searchParams.get('page') || 0), // MUI uses zero-based page index
    pageSize: Number(searchParams.get('pageSize') || 10)
  });
  const [filterModel, setFilterModel] = useState(() => {
    const filtersParam = searchParams.get('filters');
    if (filtersParam) {
      try {
        const items = JSON.parse(filtersParam);
        if (Array.isArray(items)) {
          return { items };
        }
      } catch (e) { /* ignore parse errors */ }
    }
    return { items: [] };
  });

  // Quick search state — initialised from URL so it survives a browser refresh
  const [quickSearchValue, setQuickSearchValue] = useState(() => {
    const filtersParam = searchParams.get('filters');
    if (filtersParam) {
      try {
        const items = JSON.parse(filtersParam);
        if (Array.isArray(items)) {
          const textFilter = items.find(f => f.field === 'text' && f.operator === 'contains');
          return textFilter?.value || '';
        }
      } catch (e) { /* ignore parse errors */ }
    }
    return '';
  });

  // Get default string operators
  const stringOperators = getGridStringOperators();

  // Reusable value options for enum fields (derived from shared constants)
  const binaryAnswerOptions = Object.values(BINARY_ANSWERS).map((v) => ({ value: v, label: t(v) }));
  const ternaryAnswerOptions = Object.values(TERNARY_ANSWERS).map((v) => ({ value: v, label: t(v) }));

  // Reusable render functions
  const renderYesNoTag = (params) => {
    const value = params.value;
    if (value === BINARY_ANSWERS.YES) {
      return <Tag color="green">{t(BINARY_ANSWERS.YES)}</Tag>;
    }
    if (value === BINARY_ANSWERS.NO) {
      return <Tag color="red">{t(BINARY_ANSWERS.NO)}</Tag>;
    }
    return '-';
  };

  const renderYesNoOtherTag = (params) => {
    const value = params.value;
    if (value === TERNARY_ANSWERS.YES) {
      return <Tag color="green">{t(TERNARY_ANSWERS.YES)}</Tag>;
    }
    if (value === TERNARY_ANSWERS.NO) {
      return <Tag color="red">{t(TERNARY_ANSWERS.NO)}</Tag>;
    }
    if (value) {
      return <Tag color="blue">{t(value)}</Tag>;
    }
    return '-';
  };

  const renderBooleanTag = (params) => {
    const value = params.value;
    if (value === true) {
      return <Tag color="green">Yes</Tag>;
    }
    if (value === false) {
      return <Tag color="red">No</Tag>;
    }
    return '-';
  };

  const renderTextCell = (params) => {
    return <div className="text-capitalize-first" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '1.5', maxHeight: '3em' }}>{params.value || '-'}</div>;
  };

  // Columns definition
  const columns = [
    {
      field: 'title',
      headerName: 'Title',
      filterable: true,
      width: 180,
      minWidth: 150,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: (params) => (
        <Link
          to={replaceUrlParams(NAV_NDES_VIEW, { ndeId: params.row.id })}
          onClick={e => e.stopPropagation()}
          style={{ color: 'inherit', textDecoration: 'none', display: 'block', width: '100%' }}
        >
          <div className="text-capitalize-first" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '1.5', maxHeight: '3em' }}>
            {params.value || '-'}
          </div>
        </Link>
      )
    },
    {
      field: 'provider',
      headerName: 'Provider',
      filterable: true,
      width: 110,
      minWidth: 80,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'type',
      headerName: 'NDE Type',
      filterable: true,
      width: 120,
      minWidth: 100,
      type: 'singleSelect',
      valueOptions: Object.values(NDE_TYPES).map((v) => ({ value: v, label: t(v, { context: 'type' }) })),
      description: t('NDES can be classified into different types based on the experience. Possible values are: Peaceful, Distressing, Neutral, Mixed.'),
      renderCell: (params) => {
        return params.value ? <NdeTypeTag type={params.value} /> : '-';
      }
    },
    {
      field: 'text',
      headerName: 'NDE Text',
      filterable: true,
      sortable: false,
      flex: 1,
      minWidth: 250,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: (params) => {
        const text = params.value;
        if (!text) {
          return '-';
        }
        return (
          <div className="text-capitalize-first" style={{ whiteSpace: 'normal', lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {text}
          </div>
        );
      }
    },
    {
      field: 'environment',
      headerName: 'Environment',
      filterable: true,
      width: 180,
      minWidth: 140,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: (params) => {
        if (!params.value || params.value.length === 0) {
          return '-';
        }
        const values = Array.isArray(params.value) ? params.value : params.value.split(',');
        const colorMap = {
          [ENVIRONMENTS.EARTH]: 'blue',
          [ENVIRONMENTS.PLACE_OF_LOVE]: 'green',
          [ENVIRONMENTS.PLACE_OF_SUFFERING]: 'red',
          [ENVIRONMENTS.VOID]: 'purple',
          [ENVIRONMENTS.OTHER]: 'default'
        };
        return values.map((val, index) => (
          <Tag key={index} color={colorMap[val.trim()] || 'default'} className="text-capitalize-first">
            {t(val.trim(), { context: 'environments' })}
          </Tag>
        ));
      }
    },
    {
      field: 'isUnusual',
      headerName: 'Unusual',
      filterable: true,
      width: 100,
      minWidth: 80,
      type: 'boolean',
      filterOperators: getGridBooleanOperators(),
      renderCell: renderBooleanTag
    },
    {
      field: 'gender',
      headerName: 'Gender',
      filterable: true,
      width: 90,
      minWidth: 80,
      type: 'singleSelect',
      valueOptions: Object.values(GENDERS).map((v) => ({ value: v, label: t(v, { context: 'gender' }) })),
      renderCell: (params) => params.value ? t(params.value, { context: 'gender' }) : '-'
    },
    {
      field: 'sexuality',
      headerName: 'Sexuality',
      filterable: true,
      width: 110,
      minWidth: 90,
      type: 'singleSelect',
      valueOptions: Object.values(SEXUALITIES).map((v) => ({ value: v, label: t(v, { context: 'sexuality' }) })),
      renderCell: renderTextCell
    },
    {
      field: 'deathCause',
      headerName: 'Death Cause',
      filterable: true,
      width: 130,
      minWidth: 100,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'ndeDuration',
      headerName: 'NDE Duration (min)',
      filterable: true,
      width: 160,
      minWidth: 140,
      type: 'number',
      filterOperators: getGridNumericOperators()
    },
    {
      field: 'deathDuration',
      headerName: 'Death Duration (min)',
      filterable: true,
      width: 170,
      minWidth: 150,
      type: 'number',
      filterOperators: getGridNumericOperators()
    },
    {
      field: 'medications',
      headerName: 'Medications',
      filterable: true,
      width: 150,
      minWidth: 120,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'hadLifeReview',
      headerName: 'Life Review',
      filterable: true,
      width: 110,
      minWidth: 90,
      type: 'singleSelect',
      valueOptions: ternaryAnswerOptions,
      renderCell: renderYesNoOtherTag
    },
    {
      field: 'bodyFunctions',
      headerName: 'Body Functions Stopped',
      filterable: true,
      width: 180,
      minWidth: 150,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: (params) => {
        if (!params.value || params.value.length === 0) {
          return '-';
        }

        const values = Array.isArray(params.value) ? params.value : params.value.split(',');
        return values
          .map((val) => t(val.trim(), { context: 'bodyFunctions' }))
          .join(', ');
      }
    },
    {
      field: 'hadObe',
      headerName: 'OBE',
      filterable: true,
      width: 80,
      minWidth: 70,
      type: 'singleSelect',
      valueOptions: ternaryAnswerOptions,
      renderCell: renderYesNoOtherTag
    },
    {
      field: 'timePerception',
      headerName: 'Time Perception',
      filterable: true,
      width: 140,
      minWidth: 120,
      type: 'singleSelect',
      valueOptions: Object.values(TIME_PERCEPTIONS).map((v) => ({ value: v, label: t(v, { context: 'timePerceptions' }) })),
      renderCell: (params) => params.value ? t(params.value, { context: 'timePerceptions' }) : '-'
    },
    {
      field: 'timePerceptionText',
      headerName: 'Time Perception Details',
      filterable: true,
      flex: 1,
      minWidth: 180,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'hadObeText',
      headerName: 'OBE Details',
      filterable: true,
      flex: 1,
      minWidth: 150,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'hadLifeReviewText',
      headerName: 'Life Review Details',
      filterable: true,
      flex: 1,
      minWidth: 160,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'realityPerception',
      headerName: 'Reality Perception',
      filterable: true,
      width: 150,
      minWidth: 130,
      type: 'singleSelect',
      valueOptions: Object.values(REALITY_PERCEPTIONS).map((v) => ({ value: v, label: t(v, { context: 'realityPerceptions' }) })),
      renderCell: (params) => params.value ? t(params.value, { context: 'realityPerceptions' }) : '-'
    },
    {
      field: 'realityPerceptionText',
      headerName: 'Reality Perception Details',
      filterable: true,
      flex: 1,
      minWidth: 180,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'environmentText',
      headerName: 'Environment Details',
      filterable: true,
      flex: 1,
      minWidth: 160,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'feltUnity',
      headerName: 'Felt Unity',
      filterable: true,
      width: 100,
      minWidth: 80,
      type: 'singleSelect',
      valueOptions: ternaryAnswerOptions,
      renderCell: renderYesNoOtherTag
    },
    {
      field: 'feltUnityText',
      headerName: 'Felt Unity Details',
      filterable: true,
      flex: 1,
      minWidth: 150,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'sawBrightLight',
      headerName: 'Bright Light',
      filterable: true,
      width: 110,
      minWidth: 90,
      type: 'boolean',
      filterOperators: getGridBooleanOperators(),
      renderCell: renderBooleanTag
    },
    {
      field: 'feltSenseOfUnderstanding',
      headerName: 'Felt Understanding',
      filterable: true,
      width: 140,
      minWidth: 120,
      type: 'singleSelect',
      valueOptions: ternaryAnswerOptions,
      renderCell: renderYesNoOtherTag
    },
    {
      field: 'feltSenseOfUnderstandingText',
      headerName: 'Understanding Details',
      filterable: true,
      flex: 1,
      minWidth: 160,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'feltOtherEntities',
      headerName: 'Other Entities',
      filterable: true,
      width: 120,
      minWidth: 100,
      type: 'singleSelect',
      valueOptions: binaryAnswerOptions,
      renderCell: renderYesNoTag
    },
    {
      field: 'feltOtherEntitiesText',
      headerName: 'Entities Details',
      filterable: true,
      flex: 1,
      minWidth: 150,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'entityTypes',
      headerName: 'Entity Types',
      filterable: true,
      width: 150,
      minWidth: 120,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: (params) => {
        if (!params.value || params.value.length === 0) {
          return '-';
        }
        const values = Array.isArray(params.value) ? params.value : params.value.split(',');
        return values
          .map((val) => t(val.trim(), { context: 'entityTypes' }))
          .join(', ');
      }
    },
    {
      field: 'hadVisionsOfFuture',
      headerName: 'Future Visions',
      filterable: true,
      width: 120,
      minWidth: 100,
      type: 'singleSelect',
      valueOptions: ternaryAnswerOptions,
      renderCell: renderYesNoOtherTag
    },
    {
      field: 'hadVisionsOfFutureText',
      headerName: 'Future Visions Details',
      filterable: true,
      flex: 1,
      minWidth: 160,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'visionsCameTrue',
      headerName: 'Visions Came True',
      filterable: true,
      width: 140,
      minWidth: 120,
      type: 'singleSelect',
      valueOptions: ternaryAnswerOptions,
      renderCell: renderYesNoOtherTag
    },
    {
      field: 'feltJudged',
      headerName: 'Felt Judged',
      filterable: true,
      width: 140,
      minWidth: 120,
      type: 'singleSelect',
      valueOptions: Object.values(FELT_JUDGED).map((v) => ({ value: v, label: t(v, { context: 'feltJudged' }) })),
      renderCell: (params) => {
        const value = params.value;
        if (value === FELT_JUDGED.NO) {
          return <Tag color="red">{t(FELT_JUDGED.NO)}</Tag>;
        }
        if (value) {
          return <Tag color="blue">{t(value, { context: 'feltJudged' })}</Tag>;
        }
        return '-';
      }
    },
    {
      field: 'feltJudgedText',
      headerName: 'Judgement Details',
      filterable: true,
      flex: 1,
      minWidth: 150,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'hadChoiceToReturn',
      headerName: 'Choice to Return',
      filterable: true,
      width: 140,
      minWidth: 120,
      type: 'singleSelect',
      valueOptions: binaryAnswerOptions,
      renderCell: renderYesNoTag
    },
    {
      field: 'hadChoiceToReturnText',
      headerName: 'Choice Details',
      filterable: true,
      flex: 1,
      minWidth: 150,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'escapedNegativeNde',
      headerName: 'Escaped Negative NDE',
      filterable: true,
      width: 170,
      minWidth: 150,
      type: 'singleSelect',
      valueOptions: binaryAnswerOptions,
      renderCell: renderYesNoTag
    },
    {
      field: 'escapedNegativeNdeText',
      headerName: 'Escape Details',
      filterable: true,
      flex: 1,
      minWidth: 150,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'feltDeservedNegativeNde',
      headerName: 'Deserved Negative NDE',
      filterable: true,
      width: 180,
      minWidth: 160,
      type: 'singleSelect',
      valueOptions: binaryAnswerOptions,
      renderCell: renderYesNoTag
    },
    {
      field: 'feltDeservedNegativeNdeText',
      headerName: 'Deservedness Details',
      filterable: true,
      flex: 1,
      minWidth: 160,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'sawScenesFromElsewhere',
      headerName: 'Scenes Elsewhere',
      filterable: true,
      width: 140,
      minWidth: 120,
      type: 'singleSelect',
      valueOptions: binaryAnswerOptions,
      renderCell: renderYesNoTag
    },
    {
      field: 'sawScenesFromElsewhereText',
      headerName: 'Scenes Details',
      filterable: true,
      flex: 1,
      minWidth: 150,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'wasHealed',
      headerName: 'Healed',
      filterable: true,
      width: 90,
      minWidth: 80,
      type: 'singleSelect',
      valueOptions: binaryAnswerOptions,
      renderCell: renderYesNoTag
    },
    {
      field: 'wasHealedText',
      headerName: 'Healing Details',
      filterable: true,
      flex: 1,
      minWidth: 150,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'religionBefore',
      headerName: 'Religion Before',
      filterable: true,
      width: 130,
      minWidth: 110,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')]
    },
    {
      field: 'religionNow',
      headerName: 'Religion Now',
      filterable: true,
      width: 120,
      minWidth: 100,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')]
    },
    {
      field: 'ndeEffects',
      headerName: 'NDE Effects',
      filterable: true,
      flex: 1,
      minWidth: 150,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'age',
      headerName: 'Age',
      filterable: true,
      width: 80,
      minWidth: 60,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'graysonScale',
      headerName: 'Greyson Scale',
      filterable: true,
      width: 130,
      minWidth: 110,
      type: 'number',
      filterOperators: getGridNumericOperators(),
      description: 'Score of 7+ on the Greyson Scale indicates a genuine NDE',
      renderCell: (params) => {
        const value = params.value;
        if (value == null) {
          return '-';
        }
        const color = value >= 7 ? '#52c41a' : '#faad14';
        return (
          <Space size={6} align="center">
            <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color }} />
            <span style={{ fontWeight: 500 }}>{value}</span>
            <span style={{ color: '#bbb', fontSize: 11 }}>/32</span>
          </Space>
        );
      }
    },
    {
      field: 'emotions',
      headerName: 'Emotions',
      filterable: true,
      width: 200,
      minWidth: 160,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'country',
      headerName: 'Country',
      filterable: true,
      width: 120,
      minWidth: 100,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'ethnicity',
      headerName: 'Ethnicity',
      filterable: true,
      width: 120,
      minWidth: 100,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'experiencedClinicalDeath',
      headerName: 'Clinical Death',
      filterable: true,
      width: 120,
      minWidth: 100,
      type: 'boolean',
      filterOperators: getGridBooleanOperators(),
      renderCell: renderBooleanTag
    },
    {
      field: 'sawHellishImagery',
      headerName: 'Hellish Imagery',
      filterable: true,
      width: 130,
      minWidth: 110,
      type: 'boolean',
      filterOperators: getGridBooleanOperators(),
      renderCell: renderBooleanTag
    },
    {
      field: 'sawHellishImageryText',
      headerName: 'Hellish Imagery Details',
      filterable: true,
      flex: 1,
      minWidth: 170,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'developedPsychicAbilities',
      headerName: 'Psychic Abilities',
      filterable: true,
      width: 140,
      minWidth: 120,
      type: 'boolean',
      filterOperators: getGridBooleanOperators(),
      renderCell: renderBooleanTag
    },
    {
      field: 'developedPsychicAbilitiesText',
      headerName: 'Psychic Abilities Details',
      filterable: true,
      flex: 1,
      minWidth: 180,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'hadSharedExperience',
      headerName: 'Shared Experience',
      filterable: true,
      width: 150,
      minWidth: 130,
      type: 'boolean',
      filterOperators: getGridBooleanOperators(),
      renderCell: renderBooleanTag
    },
    {
      field: 'hadSharedExperienceText',
      headerName: 'Shared Experience Details',
      filterable: true,
      flex: 1,
      minWidth: 180,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'occupation',
      headerName: 'Occupation',
      filterable: true,
      width: 130,
      minWidth: 110,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'wasResuscitated',
      headerName: 'Resuscitated',
      filterable: true,
      width: 120,
      minWidth: 100,
      type: 'boolean',
      filterOperators: getGridBooleanOperators(),
      renderCell: renderBooleanTag
    },
    {
      field: 'hadAnesthesia',
      headerName: 'Under Anesthesia',
      filterable: true,
      width: 140,
      minWidth: 120,
      type: 'boolean',
      filterOperators: getGridBooleanOperators(),
      renderCell: renderBooleanTag
    },
    {
      field: 'hadPriorNde',
      headerName: 'Prior NDE',
      filterable: true,
      width: 100,
      minWidth: 90,
      type: 'boolean',
      filterOperators: getGridBooleanOperators(),
      renderCell: renderBooleanTag
    },
    {
      field: 'sawTunnel',
      headerName: 'Saw Tunnel',
      filterable: true,
      width: 110,
      minWidth: 90,
      type: 'boolean',
      filterOperators: getGridBooleanOperators(),
      renderCell: renderBooleanTag
    },
    {
      field: 'sawTunnelText',
      headerName: 'Tunnel Details',
      filterable: true,
      flex: 1,
      minWidth: 150,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'didNotWantToReturn',
      headerName: 'Didn\'t Want to Return',
      filterable: true,
      width: 160,
      minWidth: 140,
      type: 'boolean',
      filterOperators: getGridBooleanOperators(),
      renderCell: renderBooleanTag
    },
    {
      field: 'didNotWantToReturnText',
      headerName: 'Return Reluctance Details',
      filterable: true,
      flex: 1,
      minWidth: 180,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'lostFearOfDeath',
      headerName: 'Lost Fear of Death',
      filterable: true,
      width: 150,
      minWidth: 130,
      type: 'boolean',
      filterOperators: getGridBooleanOperators(),
      renderCell: renderBooleanTag
    },
    {
      field: 'lostFearOfDeathText',
      headerName: 'Fear of Death Details',
      filterable: true,
      flex: 1,
      minWidth: 170,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'sharedExperiencePublicly',
      headerName: 'Shared Publicly',
      filterable: true,
      width: 130,
      minWidth: 110,
      type: 'boolean',
      filterOperators: getGridBooleanOperators(),
      renderCell: renderBooleanTag
    },
    {
      field: 'sawAnimals',
      headerName: 'Saw Animals',
      filterable: true,
      width: 110,
      minWidth: 90,
      type: 'boolean',
      filterOperators: getGridBooleanOperators(),
      renderCell: renderBooleanTag
    },
    {
      field: 'sawAnimalsText',
      headerName: 'Animals Details',
      filterable: true,
      flex: 1,
      minWidth: 150,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    },
    {
      field: 'heardMusic',
      headerName: 'Heard Music',
      filterable: true,
      width: 110,
      minWidth: 90,
      type: 'boolean',
      filterOperators: getGridBooleanOperators(),
      renderCell: renderBooleanTag
    },
    {
      field: 'heardMusicText',
      headerName: 'Music Details',
      filterable: true,
      flex: 1,
      minWidth: 150,
      filterOperators: [stringOperators.find((op) => op.value === 'contains')],
      renderCell: renderTextCell
    }
  ];

  const filterableColumns = columns.filter(c => c.filterable);

  // Handles filter model changes from the custom MultiFilterPanel.
  // We do NOT pass filterModel as a controlled prop to DataGrid to avoid MUI X free tier
  // trimming multi-filter state via its onFilterModelChange callback.
  const handleFilterModelChange = (newFilterModel) => {
    const serializeItems = (items) =>
      items
        .filter(f => f.field && f.value != null && f.value !== '')
        .map(f => `${f.field}:${f.operator}:${JSON.stringify(f.value)}`)
        .sort()
        .join('|');

    if (serializeItems(newFilterModel.items) !== serializeItems(filterModel.items)) {
      setPaginationModel(prev => ({ ...prev, page: 0 }));
    }

    setFilterModel(newFilterModel);
    const textFilter = newFilterModel.items.find((f) => f.field === 'text' && f.operator === 'contains');
    setQuickSearchValue(textFilter?.value || '');
  };

  // Build initial column visibility model from the columns definition
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    const model = {};
    columns.forEach((col) => {
      model[col.field] = DEFAULT_VISIBLE_FIELDS.includes(col.field);
    });
    return model;
  });

  const handleSuggestedSearch = ({ filters, extraColumns }) => {
    handleFilterModelChange({ items: filters });
    const model = {};
    columns.forEach((col) => {
      model[col.field] = DEFAULT_VISIBLE_FIELDS.includes(col.field) || extraColumns.includes(col.field);
    });
    setColumnVisibilityModel(model);
  };

  const quickSearchDebounceRef = useRef(null);

  // Quick search handler. Updates the filter model's 'text' contains filter
  const handleQuickSearch = useCallback((value) => {
    setQuickSearchValue(value);

    clearTimeout(quickSearchDebounceRef.current);
    quickSearchDebounceRef.current = setTimeout(() => {
      setFilterModel((prev) => {
        const otherFilters = prev.items.filter((f) => !(f.field === 'text' && f.operator === 'contains'));
        if (value) {
          return { items: [...otherFilters, { field: 'text', operator: 'contains', value }] };
        }
        return { items: otherFilters };
      });

      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 400);
  }, []);

  // Fetch data from the backend
  const fetchData = () => {
    let urlSearchParams = [];
    urlSearchParams.push(`skip=${(Number(paginationModel.page * paginationModel.pageSize))}`);
    urlSearchParams.push(`take=${Number(paginationModel.pageSize)}`);

    const body = {
      params: [
        ...filterModel.items
          .filter((filter) => filter.value != null && filter.value !== '') // Include only filters with values
          .map((filter) => ({
            field: filter.field,
            operator: filter.operator,
            value: filter.value
          }))
      ]
    };

    // Add sorting
    if (sortModel.length > 0) {
      const order = {};
      sortModel.forEach(s => {
        order[s.field] = s.sort === 'asc' ? 'ASC' : 'DESC';
      });
      body.order = order;
    }

    dispatch(fetchNdes({ urlSearchParams, body }));
  };

  // Fetch data on mount and when dependencies change
  useEffect(() => {
    fetchData();
  }, [paginationModel, filterModel, sortModel]);

  // Update search params in the URL.
  // Only write non-default values so the canonical /ndes URL stays clean
  // (writing page=0&pageSize=10 on every load makes Search Console see a redirect).
  const updateSearchParams = () => {
    const params = {};
    const validFilters = filterModel.items.filter(f => f.field && f.value != null && f.value !== '');
    const hasFilters = validFilters.length > 0;
    const hasSort = sortModel.length > 0;
    const isNonDefaultPage = paginationModel.page !== 0;
    const isNonDefaultPageSize = paginationModel.pageSize !== 10;

    // Only include page/pageSize when they differ from defaults or other params are present
    if (isNonDefaultPage || isNonDefaultPageSize || hasFilters || hasSort) {
      params.page = String(paginationModel.page);
      params.pageSize = String(paginationModel.pageSize);
    }
    if (hasFilters) {
      params.filters = JSON.stringify(validFilters.map(f => ({ field: f.field, operator: f.operator, value: f.value })));
    }
    if (hasSort) {
      params.sort = JSON.stringify(sortModel);
    }
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    updateSearchParams();
  }, [paginationModel, filterModel, sortModel]);

  const activeFilterCount = filterModel.items.filter(f => f.value != null && f.value !== '').length;
  const hasActiveFilters = activeFilterCount > 0;

  return (
    <Fragment>
      <Row className="mb-16" align="middle" justify="space-between">
        <Col flex="auto">
          <Title level={3} style={{ marginBottom: 4, fontWeight: 700 }}>NDE Database</Title>
          <Paragraph className="mb-0" style={{ color: '#888', fontSize: 14, lineHeight: '22px' }}>
            Key columns are shown by default. Use <Text strong>Columns</Text> to customize, <Text strong>Filters</Text> to narrow results, or the quick search to find text in experiences.
          </Paragraph>
        </Col>
      </Row>

      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
        <Text type="secondary" style={{ fontSize: 12, marginRight: 2, whiteSpace: 'nowrap' }}>Try:</Text>
        {SUGGESTED_SEARCHES.map((s, i) => (
          <Tag
            key={i}
            icon={<SearchOutlined style={{ fontSize: 11 }} />}
            style={{ cursor: 'pointer', margin: 0, fontSize: 12, padding: '2px 8px', borderRadius: 20, userSelect: 'none' }}
            onClick={() => handleSuggestedSearch(s)}
          >
            {s.label}
          </Tag>
        ))}
      </div>

      <div style={{ width: '100%', borderRadius: 14, overflow: 'hidden', border: '1px solid #eee', boxShadow: '0 1px 10px rgba(0,0,0,0.04)' }} className="bg-color-black-0 bg-color-dark-100 mb-32">
        <ThemeProvider theme={theme}>
          <DataGrid
            rows={ndes?.data || []}
            rowCount={ndes?.total ?? (paginationModel.page + 1) * paginationModel.pageSize + 1}
            loading={isFetchingNdes}
            columns={columns}
            pagination
            paginationMode="server"
            pageSizeOptions={[10, 25, 50, 100]}
            getRowClassName={(params) => 'cursor-pointer'}
            filterMode="server"
            paginationModel={paginationModel}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
            slots={{ toolbar: CustomToolbar, filterPanel: MultiFilterPanel }}
            slotProps={{
              filterPanel: { filterableColumns, onApplyFilters: handleFilterModelChange, filterModel },
              toolbar: {
                onClearFilters: () => {
                  setFilterModel({ items: [] });
                  setQuickSearchValue('');
                },
                hasActiveFilters,
                activeFilterCount,
                totalResults: ndes?.total,
                onQuickSearch: handleQuickSearch,
                quickSearchValue
              }
            }}
            localeText={{ toolbarColumns: 'Columns' }}
            onRowClick={(row) => {
              navigate(replaceUrlParams(NAV_NDES_VIEW, { ndeId: row.id }));
            }}
            onPaginationModelChange={(newModel) => setPaginationModel(newModel)}
            disableRowSelectionOnClick
            disableColumnMenu={false}
            autoHeight
            getRowHeight={() => 'auto'}
            sx={{
              minHeight: 500,
              '& .MuiDataGrid-overlayWrapper': {
                minHeight: 200
              }
            }}
          />
        </ThemeProvider>
      </div>
    </Fragment>
  );
};

export default NdesTable;
